import { useMemo, useState, type FormEvent } from "react";
import { CEFR, catalog as handbook, lessons, SECTION_NAMES, type SectionId } from "../../shared/catalog";
import { timelineShape } from "../../shared/lesson-model";
import { api, ApiError } from "../lib/api";
import { Link, navigate, useLocation } from "../lib/router";
import { levelIn, scoreLessons } from "../lib/search";
import { useSession } from "../lib/session";
import { LevelLadder } from "../components/LevelLadder";
import { StageStrip } from "../components/StageStrip";
import { Icon } from "../components/Icon";
import type { RecommendResult } from "../lib/types";

const EXAMPLES = [
  "Form 3, about 40 students, B1, only 30 minutes after assembly and no projector. They switch off during writing.",
  "Online group of 6 adults at A2 who need confidence speaking before job interviews.",
  "Mixed-ability class revising for exams next week; I want something lively.",
];

type Pick = RecommendResult["picks"][number] & { local?: boolean };

export default function Library() {
  const { search } = useLocation();
  const { me, favorites, toggleFavorite } = useSession();
  const [section, setSection] = useState<SectionId | "all">((search.get("section") as SectionId) || "all");
  const [level, setLevel] = useState<string>(search.get("level") || "");
  const [q, setQ] = useState(search.get("q") || "");
  const [savedOnly, setSavedOnly] = useState(false);

  const [ask, setAsk] = useState("");
  const [picks, setPicks] = useState<Pick[] | null>(null);
  const [asking, setAsking] = useState(false);
  const [askNote, setAskNote] = useState("");

  const list = useMemo(() => {
    let xs = q.trim() ? scoreLessons(q).map((x) => x.lesson) : lessons;
    if (section !== "all") xs = xs.filter((l) => l.section === section);
    if (level) xs = xs.filter((l) => levelIn(l, level));
    if (savedOnly) xs = xs.filter((l) => favorites.has(l.id));
    return xs;
  }, [q, section, level, savedOnly, favorites]);

  const concierge = async (e: FormEvent) => {
    e.preventDefault();
    if (ask.trim().length < 3) return;
    setAsking(true);
    setAskNote("");
    try {
      const r = await api.recommend(ask.trim());
      setPicks(r.picks);
    } catch (err) {
      const local = scoreLessons(ask).slice(0, 3).map(({ lesson }) => ({ lessonId: lesson.id, why: lesson.objective, suggested: {}, local: true }));
      setPicks(local);
      setAskNote(err instanceof ApiError && err.code === "LIMIT_REACHED" ? "Daily concierge limit reached, so these are keyword matches." : "The AI concierge is unavailable right now, so these are keyword matches.");
    } finally {
      setAsking(false);
    }
  };

  const studioLink = (p: Pick) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(p.suggested ?? {})) if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
    const qs = s.toString();
    return `/studio/${p.lessonId}${qs ? "?" + qs : ""}`;
  };

  return (
    <div className="page library">
      <section className="concierge" aria-labelledby="concierge-h">
        <h1 id="concierge-h" className="hero-title">Tell us about Monday's class.<br />We'll find the lesson and fit it to them.</h1>
        <form onSubmit={concierge} className="concierge-form">
          <label htmlFor="ask" className="visually-hidden">Describe your class and what you need</label>
          <textarea id="ask" rows={3} value={ask} onChange={(e) => setAsk(e.target.value)} maxLength={500}
            placeholder="Level, class size, time, what you have in the room, what they find hard…" />
          <div className="concierge-actions">
            <button className="btn btn-primary" disabled={asking || ask.trim().length < 3}>
              <Icon name="spark" /> {asking ? "Finding lessons…" : "Find my lesson"}
            </button>
            <span className="muted small">or try:</span>
            {EXAMPLES.map((ex, i) => (
              <button type="button" key={i} className="btn-link small example" onClick={() => setAsk(ex)}>
                {["Big class, short period", "Online adults", "Exam revision"][i]}
              </button>
            ))}
          </div>
        </form>
        {picks && (
          <div className="picks" aria-live="polite">
            {askNote && <p className="muted small">{askNote}</p>}
            {picks.length === 0 && <p>No close match. Try naming the skill (grammar, reading, speaking…) or the level.</p>}
            <ol>
              {picks.map((p) => {
                const l = lessons.find((x) => x.id === p.lessonId)!;
                return (
                  <li key={p.lessonId} className="pick">
                    <div>
                      <p className="pick-title"><span className="code">{l.id}</span> {l.activity}</p>
                      <p className="pick-topic">{l.topic}</p>
                      <p className="pick-why">{p.why}</p>
                    </div>
                    <div className="pick-actions">
                      <Link className="btn btn-primary" to={studioLink(p)}>Adapt for my class</Link>
                      <Link className="btn btn-quiet" to={`/lesson/${l.id}`}>Read original</Link>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </section>

      <section aria-labelledby="all-h" className="catalogue">
        <div className="catalogue-head">
          <h2 id="all-h">All 50 lessons</h2>
          <input type="search" className="search" placeholder="Search: passive, debate, idioms, B2…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search lessons (type a code like C4 and press Enter to open it)"
            onKeyDown={(e) => { const code = q.trim().toUpperCase(); if (e.key === "Enter" && lessons.some((l) => l.id === code)) navigate(`/lesson/${code}`); }} />
        </div>
        <div className="tabs" role="tablist" aria-label="Section">
          <button role="tab" aria-selected={section === "all"} className={section === "all" ? "on" : ""} onClick={() => setSection("all")}>All</button>
          {handbook.sections.map((s) => (
            <button role="tab" key={s.id} aria-selected={section === s.id} className={section === s.id ? "on" : ""} onClick={() => setSection(s.id)}>
              <span className="tab-letter">{s.id}</span> {SECTION_NAMES[s.id]}
            </button>
          ))}
        </div>
        <div className="filters">
          <span className="muted small" id="lvl-f">Suits level</span>
          <div className="seg-control seg-small" role="radiogroup" aria-labelledby="lvl-f">
            <button role="radio" aria-checked={!level} className={!level ? "on" : ""} onClick={() => setLevel("")}>Any</button>
            {CEFR.slice(1, 5).map((l) => (
              <button role="radio" key={l} aria-checked={level === l} className={level === l ? "on" : ""} onClick={() => setLevel(l)}>{l}</button>
            ))}
          </div>
          {me?.user && (
            <label className="check small">
              <input type="checkbox" checked={savedOnly} onChange={(e) => setSavedOnly(e.target.checked)} /> Saved only
            </label>
          )}
          <span className="muted small count">{list.length} lesson{list.length === 1 ? "" : "s"}</span>
        </div>

        <ul className="register">
          {list.map((l) => (
            <li key={l.id} className="row">
              <Link to={`/lesson/${l.id}`} className="row-link">
                <span className="row-code">{l.id}</span>
                <span className="row-main">
                  <span className="row-title">{l.activity}{(me?.previewLessons ?? ["A1"]).includes(l.id) && <span className="sample-badge">Free sample</span>}</span>
                  <span className="row-topic">{l.topic}</span>
                </span>
                <span className="row-level">
                  <LevelLadder range={[l.levelIndex[0], l.levelIndex[1]]} mixed={l.mixedLevel} />
                  <span className="small muted">{l.level}</span>
                </span>
                <span className="row-strip">
                  <StageStrip stages={timelineShape(l.duration)} size="xs" />
                  <span className="small muted">{l.durationLabel}</span>
                </span>
              </Link>
              {me?.user && (
                <button className={`fav${favorites.has(l.id) ? " on" : ""}`} aria-pressed={favorites.has(l.id)} aria-label={favorites.has(l.id) ? `Unsave ${l.id}` : `Save ${l.id}`}
                  onClick={() => void toggleFavorite(l.id)}>
                  <Icon name="heart" />
                </button>
              )}
            </li>
          ))}
        </ul>
        {list.length === 0 && (
          <div className="empty">
            <p>No lessons match those filters.</p>
            <button className="btn btn-quiet" onClick={() => { setQ(""); setSection("all"); setLevel(""); setSavedOnly(false); }}>Clear filters</button>
          </div>
        )}
      </section>
    </div>
  );
}
